"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

import { useEffect, useId, useInsertionEffect, type ComponentProps, type CSSProperties, type ReactNode } from "react";
import { Progress as ProgressPrimitive } from "radix-ui";
import { animate, motion, useMotionValue, useTransform, type MotionValue } from "motion/react";
import { useLocale } from "@/components/wingo/hooks/use-locale";
import { springs, useMotionTransition } from "@/components/wingo/lib/motion";
import { formatNumber } from "@/components/wingo/lib/number-format";
import { colorVars, toneClasses, type Tone } from "@/components/wingo/lib/tones";
import { useDefaults, useUIConfig } from "@/components/wingo/lib/ui-config";
import { cn } from "@/components/wingo/lib/utils";
import { withRef } from "@/components/wingo/lib/with-ref";

export type ProgressVariant = "linear" | "segmented" | "ring";
export type ProgressSize = "xs" | "sm" | "md" | "lg";
export type ProgressRadius = "none" | "sm" | "full";
export type ProgressValueFormat = "percent" | "fraction" | "value";
export type ProgressLabelPosition = "top" | "side";
export type ProgressSlot =
  | "header"
  | "label"
  | "value"
  | "track"
  | "indicator"
  | "segment"
  | "ring"
  | "description";
export type ProgressThresholds = { warning?: number; danger?: number };
export type { Tone as ProgressTone };

// linear track heights: 2 / 4 / 6 / 8px. xs is the hairline for "refreshing" bars on top of lists
const TRACK_SIZES: Record<ProgressSize, string> = {
  xs: "h-0.5",
  sm: "h-1",
  md: "h-1.5",
  lg: "h-2",
};

const RADII: Record<ProgressRadius, string> = {
  none: "rounded-none",
  sm: "rounded-[2px]",
  full: "rounded-full",
};

// gap between the header, the bar and the description
const STACK_GAPS: Record<ProgressSize, string> = {
  xs: "gap-1.5",
  sm: "gap-1.5",
  md: "gap-2",
  lg: "gap-2.5",
};

const SEGMENT_GAPS: Record<ProgressSize, string> = {
  xs: "gap-0.5",
  sm: "gap-1",
  md: "gap-1",
  lg: "gap-1.5",
};

// ring diameter and stroke in px
const RING_SIZES: Record<ProgressSize, number> = { xs: 24, sm: 32, md: 48, lg: 64 };
const RING_STROKES: Record<ProgressSize, number> = { xs: 3, sm: 3, md: 4, lg: 6 };
// the centered value in px; xs is too small for text, its value moves next to the ring
const RING_TEXT: Record<ProgressSize, number> = { xs: 0, sm: 10, md: 14, lg: 15 };

// the centered text shrinks until it fits inside the stroke ("64 / 100" in a 48px ring)
function ringFontSize(content: ReactNode, px: number, stroke: number, named: ProgressSize | null) {
  const base = named ? RING_TEXT[named] : Math.max(9, Math.round(px * 0.24));
  if (typeof content !== "string" && typeof content !== "number") return base;
  const chars = Math.max(String(content).length, 1);
  const fit = Math.floor(((px - stroke * 2) * 0.8) / (chars * 0.64));
  return Math.max(8, Math.min(base, fit));
}

// the fraction drops its spaces inside a ring: "32/50"
function ringText(text: string | null) {
  return text === null ? null : text.replace(" / ", "/");
}

const HEADER_TEXT: Record<ProgressSize, string> = {
  xs: "text-[13px]",
  sm: "text-[13px]",
  md: "text-sm",
  lg: "text-[15px]",
};

// keyframes ship with the component (consumers only get the tokens). transform and opacity only
const KEYFRAMES = `@keyframes ui-progress-slide{from{transform:translateX(-100%)}to{transform:translateX(250%)}}
@keyframes ui-progress-spin{to{transform:rotate(360deg)}}
@keyframes ui-progress-stripes{from{transform:translateX(-1rem)}to{transform:translateX(0)}}
.ui-progress-slide{animation:ui-progress-slide 1.4s var(--motion-ease-in-out,cubic-bezier(.65,0,.35,1)) infinite}
.ui-progress-spin{animation:ui-progress-spin .9s linear infinite}
.ui-progress-stripes{animation:ui-progress-stripes 1s linear infinite}
@media (prefers-reduced-motion:reduce){.ui-progress-slide,.ui-progress-spin,.ui-progress-stripes{animation:none}}`;

// React 18 nu deduplică și nu mută în <head> un <style href precedence> (React 19 da): îl injectăm o singură dată
function injectProgressKeyframes() {
  if (typeof document === "undefined" || document.getElementById("wingo-ui-progress")) return;
  const style = document.createElement("style");
  style.id = "wingo-ui-progress";
  style.textContent = KEYFRAMES;
  document.head.appendChild(style);
}

function Keyframes() {
  useInsertionEffect(injectProgressKeyframes, []);
  return null;
}

// ---------- value helpers ----------

type Resolved = {
  // null while indeterminate
  value: number | null;
  max: number;
  // 0..1 for drawing
  fraction: number | null;
};

function resolve(value: number | null | undefined, max: number | undefined): Resolved {
  const safeMax = typeof max === "number" && Number.isFinite(max) && max > 0 ? max : null;
  if (safeMax === null || value == null || !Number.isFinite(value)) {
    return { value: null, max: safeMax ?? 100, fraction: null };
  }
  const clamped = Math.min(Math.max(value, 0), safeMax);
  return { value: clamped, max: safeMax, fraction: clamped / safeMax };
}

function formatProgressValue(
  value: number,
  max: number,
  format: ProgressValueFormat,
  locale: string,
): string {
  if (format === "fraction") return `${formatNumber(value, locale)} / ${formatNumber(max, locale)}`;
  if (format === "value") return formatNumber(value, locale);
  return formatNumber(value / max, locale, { style: "percent", maximumFractionDigits: 0 });
}

function idList(...ids: (string | false | undefined)[]) {
  return ids.filter(Boolean).join(" ") || undefined;
}

// quotas: the tone turns warning / danger at or above a percentage
function thresholdTone(fraction: number | null, thresholds: ProgressThresholds | undefined): Tone | null {
  if (fraction === null || !thresholds) return null;
  const pct = fraction * 100;
  if (thresholds.danger != null && pct >= thresholds.danger) return "danger";
  if (thresholds.warning != null && pct >= thresholds.warning) return "warning";
  return null;
}

// a 0..1 motion value that glides to `target` on a spring. the first render already holds the target
// (server markup matches), later changes animate without re-rendering per frame
function useProgressMotion(target: number | null, animated: boolean): MotionValue<number> {
  const progress = useMotionValue(target ?? 0);
  const transition = useMotionTransition(springs.smooth);
  useEffect(() => {
    // indeterminate keeps the last value, so the bar glides on from there when a value arrives
    if (target === null) return;
    if (!animated) {
      progress.set(target);
      return;
    }
    const controls = animate(progress, target, transition);
    return () => controls.stop();
  }, [target, animated, transition, progress]);
  return progress;
}

// ---------- ring ----------

type RingGraphicProps = {
  px: number;
  stroke: number;
  progress: MotionValue<number>;
  indeterminate: boolean;
  className?: string;
};

function RingGraphic({ px, stroke, progress, indeterminate, className }: RingGraphicProps) {
  const r = (px - stroke) / 2;
  const c = px / 2;
  // a round cap draws a dot at 0, so an empty ring hides its indicator
  const opacity = useTransform(progress, (v) => (v > 0.001 ? 1 : 0));
  return (
    <svg
      data-slot="progress-ring"
      width={px}
      height={px}
      viewBox={`0 0 ${px} ${px}`}
      fill="none"
      aria-hidden
      className={cn("block -rotate-90 overflow-visible", className)}
    >
      <circle
        data-slot="progress-ring-track"
        cx={c}
        cy={c}
        r={r}
        strokeWidth={stroke}
        className="stroke-foreground/10"
      />
      {indeterminate ? (
        <>
          {/* reduced motion: the whole ring pulses instead of spinning */}
          <circle
            data-slot="progress-ring-indicator"
            cx={c}
            cy={c}
            r={r}
            strokeWidth={stroke}
            pathLength={1}
            strokeDasharray="0.25 1"
            strokeLinecap="round"
            className="ui-progress-spin origin-center stroke-[color:var(--tone)] [transform-box:fill-box] motion-reduce:hidden"
          />
          <circle
            cx={c}
            cy={c}
            r={r}
            strokeWidth={stroke}
            className="hidden animate-pulse stroke-[color:color-mix(in_oklab,var(--tone)_50%,transparent)] motion-reduce:block"
          />
        </>
      ) : (
        <motion.circle
          data-slot="progress-ring-indicator"
          cx={c}
          cy={c}
          r={r}
          strokeWidth={stroke}
          strokeLinecap="round"
          className="stroke-[color:var(--tone)]"
          style={{ pathLength: progress, opacity }}
        />
      )}
    </svg>
  );
}

function ringMetrics(size: ProgressSize | number | undefined, thickness: number | undefined) {
  const named = typeof size === "string" && Object.hasOwn(RING_SIZES, size) ? size : null;
  const px = named
    ? RING_SIZES[named]
    : typeof size === "number" && Number.isFinite(size)
      ? Math.max(16, size)
      : RING_SIZES.md;
  const stroke =
    typeof thickness === "number" && Number.isFinite(thickness) && thickness > 0
      ? Math.min(thickness, px / 2 - 1)
      : named
        ? RING_STROKES[named]
        : Math.max(2, Math.round(px / 12));
  return { named, px, stroke };
}

// ---------- linear ----------

type LinearFillProps = {
  progress: MotionValue<number>;
  radius: string;
  striped: boolean;
  className?: string;
};

// the fill is a full-width bar slid in on x (translate, never width): a transform like scaleX, but the rounded
// end and the stripes keep their shape at every value
function LinearFill({ progress, radius, striped, className }: LinearFillProps) {
  const x = useTransform(progress, (v) => `${(v - 1) * 100}%`);
  const opacity = useTransform(progress, (v) => (v > 0.0005 ? 1 : 0));
  return (
    <motion.div
      data-slot="progress-indicator"
      className={cn("absolute inset-0 overflow-hidden bg-[color:var(--tone)]", radius, className)}
      style={{ x, opacity }}
    >
      {striped && (
        <span
          aria-hidden
          className="ui-progress-stripes absolute inset-y-0 -inset-x-4 bg-[linear-gradient(45deg,var(--progress-stripe)_25%,transparent_25%,transparent_50%,var(--progress-stripe)_50%,var(--progress-stripe)_75%,transparent_75%,transparent)] [background-size:1rem_1rem] [--progress-stripe:color-mix(in_oklab,var(--tone-foreground)_24%,transparent)]"
        />
      )}
    </motion.div>
  );
}

function Segment({
  index,
  count,
  progress,
  radius,
  className,
  indicatorClassName,
}: {
  index: number;
  count: number;
  progress: MotionValue<number>;
  radius: string;
  className?: string;
  indicatorClassName?: string;
}) {
  // one spring for the whole bar: segments fill one after another as it passes them
  const own = useTransform(progress, (v) => Math.min(Math.max(v * count - index, 0), 1));
  return (
    <div
      data-slot="progress-segment"
      className={cn("relative h-full overflow-hidden bg-foreground/10", radius, className)}
    >
      <LinearFill progress={own} radius={radius} striped={false} className={indicatorClassName} />
    </div>
  );
}

// ---------- ProgressRing ----------

export type ProgressRingProps = Omit<ComponentProps<"div">, "children" | "color"> & {
  // current value; null (or NaN) is indeterminate
  value?: number | null;
  max?: number;
  // xs 24, sm 32, md 48, lg 64px, or any px number
  size?: ProgressSize | number;
  // stroke width in px; defaults to 3 / 3 / 4 / 6px by size
  thickness?: number;
  tone?: Tone;
  // any css color; replaces the tone
  color?: string;
  // the formatted value in the center
  showValue?: boolean;
  valueFormat?: ProgressValueFormat;
  formatValue?: (value: number, max: number) => ReactNode;
  getValueLabel?: (value: number, max: number) => string;
  // replaces the centered value: an icon, a custom figure
  children?: ReactNode;
  // value changes glide on a spring; false jumps
  animated?: boolean;
  locale?: string;
  classNames?: Partial<Record<"ring" | "content", string>>;
};

function ProgressRingRender(ringProps: ProgressRingProps) {
  const {
    value: valueProp = null,
    max: maxProp = 100,
    size = "md",
    thickness,
    tone = "neutral",
    color,
    showValue = false,
    valueFormat = "percent",
    formatValue,
    getValueLabel,
    children,
    animated = true,
    locale: localeProp,
    classNames,
    className,
    style,
    ...props
  } = useDefaults("progress-ring", ringProps);
  const uiLocale = useUIConfig().locale;
  const locale = useLocale(localeProp || uiLocale);
  const { value, max, fraction } = resolve(valueProp, maxProp);
  const progress = useProgressMotion(fraction, animated);
  const { named, px, stroke } = ringMetrics(size, thickness);
  const indeterminate = fraction === null;
  const toneClass = toneClasses[tone] ?? toneClasses.neutral;
  const text =
    value === null
      ? null
      : formatValue
        ? formatValue(value, max)
        : ringText(formatProgressValue(value, max, valueFormat, locale));
  // xs has no room for text; an icon passed as children still fits
  const content = children ?? (showValue && named !== "xs" ? text : null);
  const fontSize = children != null && named === "xs" ? undefined : ringFontSize(content, px, stroke, named);

  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      data-variant="ring"
      data-size={named ?? "custom"}
      data-state={fraction === null ? "indeterminate" : fraction >= 1 ? "complete" : "loading"}
      data-tone={color ? "custom" : tone}
      value={value}
      max={max}
      getValueLabel={getValueLabel ?? ((v, m) => formatProgressValue(v, m, "percent", locale))}
      className={cn("relative inline-grid shrink-0 place-items-center", toneClass, className)}
      style={{ width: px, height: px, ...colorVars(color), ...style }}
      {...props}
    >
      <Keyframes />
      <RingGraphic
        px={px}
        stroke={stroke}
        progress={progress}
        indeterminate={indeterminate}
        className={cn("col-start-1 row-start-1", classNames?.ring)}
      />
      {content != null && content !== false && (
        <span
          data-slot="progress-ring-content"
          aria-hidden
          className={cn(
            "col-start-1 row-start-1 flex size-full items-center justify-center whitespace-nowrap font-semibold leading-none tabular-nums text-foreground [&_svg]:size-[45%]",
            classNames?.content,
          )}
          style={{ fontSize }}
        >
          {content}
        </span>
      )}
    </ProgressPrimitive.Root>
  );
}

// ---------- Progress ----------

export type ProgressProps = Omit<ComponentProps<"div">, "children" | "color"> & {
  // current value; null (or NaN, or max <= 0) is indeterminate
  value?: number | null;
  max?: number;
  // a bar, a bar in equal steps, or a ring
  variant?: ProgressVariant;
  // segmented: number of equal segments; a fractional value fills a segment partly
  segments?: number;
  // linear 2 / 4 / 6 / 8px; ring 24 / 32 / 48 / 64px
  size?: ProgressSize;
  // the fill; the track stays a neutral tint
  tone?: Tone;
  // any css color; replaces the tone
  color?: string;
  // quotas: percentages at which the tone turns warning / danger
  thresholds?: ProgressThresholds;
  // linear and segmented ends: square, 2px, pill
  radius?: ProgressRadius;
  label?: ReactNode;
  description?: ReactNode;
  // the formatted value at the end of the header (linear) or inside the ring
  showValue?: boolean;
  valueFormat?: ProgressValueFormat;
  formatValue?: (value: number, max: number) => ReactNode;
  // aria-valuetext; defaults to the percent
  getValueLabel?: (value: number, max: number) => string;
  // ring: stroke width in px; defaults to 3 / 3 / 4 / 6px by size
  thickness?: number;
  // ring: top stacks label, ring, description; side puts the text to the right (kpi rows)
  labelPosition?: ProgressLabelPosition;
  // linear: a lighter second bar behind the fill (buffered media, uploaded vs processed)
  buffer?: number;
  // linear determinate: moving diagonal stripes while below max
  striped?: boolean;
  // value changes glide on a spring; false jumps
  animated?: boolean;
  // ring: replaces the centered value
  children?: ReactNode;
  locale?: string;
  classNames?: Partial<Record<ProgressSlot, string>>;
};

const VARIANTS: ProgressVariant[] = ["linear", "segmented", "ring"];

function ProgressRender(progressProps: ProgressProps) {
  const {
    value: valueProp = null,
    max: maxProp = 100,
    variant: variantProp = "linear",
    segments: segmentsProp = 5,
    size: sizeProp = "md",
    tone: toneProp = "neutral",
    color,
    thresholds,
    radius: radiusProp = "full",
    label,
    description,
    showValue = false,
    valueFormat = "percent",
    formatValue,
    getValueLabel,
    thickness,
    labelPosition = "top",
    buffer,
    striped = false,
    animated = true,
    children,
    locale: localeProp,
    classNames,
    className,
    style,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    ...props
  } = useDefaults("progress", progressProps);
  const uiLocale = useUIConfig().locale;
  const locale = useLocale(localeProp || uiLocale);
  const labelId = useId();
  const descriptionId = useId();

  const variant = VARIANTS.includes(variantProp) ? variantProp : "linear";
  const size: ProgressSize = Object.hasOwn(TRACK_SIZES, sizeProp) ? sizeProp : "md";
  const radius = RADII[radiusProp] ?? RADII.full;
  const segments = Math.min(Math.max(Math.round(Number.isFinite(segmentsProp) ? segmentsProp : 5), 2), 24);

  const { value, max, fraction } = resolve(valueProp, maxProp);
  const indeterminate = fraction === null;
  const complete = fraction !== null && fraction >= 1;
  const progress = useProgressMotion(fraction, animated);
  const bufferFraction =
    typeof buffer === "number" && Number.isFinite(buffer) && !indeterminate
      ? Math.min(Math.max(buffer / max, 0), 1)
      : null;
  const bufferProgress = useProgressMotion(bufferFraction, animated);

  // an explicit color is the base like tone; a crossed threshold wins over both
  const warned = thresholdTone(fraction, thresholds);
  const tone: Tone = warned ?? (Object.hasOwn(toneClasses, toneProp) ? toneProp : "neutral");
  const customColor = warned ? undefined : color;

  const text =
    value === null ? null : formatValue ? formatValue(value, max) : formatProgressValue(value, max, valueFormat, locale);
  const valueLabel = getValueLabel ?? ((v: number, m: number) => formatProgressValue(v, m, "percent", locale));
  const hasLabel = label != null && label !== false && label !== "";
  const hasDescription = description != null && description !== false && description !== "";

  const a11y = {
    value,
    max,
    getValueLabel: valueLabel,
    // the progressbar is the track, so naming props passed to the root land there
    "aria-label": hasLabel ? undefined : ariaLabel,
    "aria-labelledby": idList(ariaLabelledBy, hasLabel && labelId),
    "aria-describedby": idList(hasDescription && descriptionId, ariaDescribedBy),
  };

  const labelNode = hasLabel ? (
    <span
      id={labelId}
      data-slot="progress-label"
      className={cn("min-w-0 font-medium text-foreground", HEADER_TEXT[size], classNames?.label)}
    >
      {label}
    </span>
  ) : null;

  const valueNode = (inline: boolean) =>
    showValue && text != null ? (
      <span
        data-slot="progress-value"
        aria-hidden
        className={cn(
          "shrink-0 tabular-nums text-muted-foreground",
          HEADER_TEXT[size],
          inline && "ms-auto",
          classNames?.value,
        )}
      >
        {text}
      </span>
    ) : null;

  const descriptionNode = hasDescription ? (
    <div
      id={descriptionId}
      data-slot="progress-description"
      className={cn("text-xs text-muted-foreground", classNames?.description)}
    >
      {description}
    </div>
  ) : null;

  const rootProps = {
    "data-slot": "progress",
    "data-variant": variant,
    "data-size": size,
    "data-state": indeterminate ? "indeterminate" : complete ? "complete" : "loading",
    "data-tone": customColor ? "custom" : tone,
    style: { ...colorVars(customColor), ...style } as CSSProperties,
  };

  if (variant === "ring") {
    const { px, stroke } = ringMetrics(size, thickness);
    const side = labelPosition === "side";
    // xs has no room inside the ring, so its value sits with the text
    const outsideValue = size === "xs" && children == null;
    const centered =
      children ?? (showValue && size !== "xs" ? (typeof text === "string" ? ringText(text) : text) : null);
    const textColumn =
      hasLabel || hasDescription || (outsideValue && showValue) ? (
        <div
          data-slot="progress-header"
          className={cn(
            "flex min-w-0 flex-col gap-0.5",
            side ? "items-start" : "items-center text-center",
            classNames?.header,
          )}
        >
          {labelNode}
          {outsideValue && valueNode(false)}
          {descriptionNode}
        </div>
      ) : null;

    return (
      <div
        {...rootProps}
        className={cn(
          "flex max-w-full",
          side ? "flex-row items-center gap-3" : "flex-col items-center gap-2",
          toneClasses[tone],
          className,
        )}
        {...props}
      >
        <Keyframes />
        {!side && hasLabel && (
          <div
            data-slot="progress-header"
            className={cn("flex min-w-0 flex-col items-center text-center", classNames?.header)}
          >
            {labelNode}
          </div>
        )}
        <ProgressPrimitive.Root
          data-slot="progress-track"
          {...a11y}
          className={cn("relative inline-grid shrink-0 place-items-center", classNames?.track)}
          style={{ width: px, height: px }}
        >
          <RingGraphic
            px={px}
            stroke={stroke}
            progress={progress}
            indeterminate={indeterminate}
            className={cn("col-start-1 row-start-1", classNames?.ring)}
          />
          {centered != null && centered !== false && (
            <span
              data-slot="progress-ring-content"
              aria-hidden
              className={cn(
                "col-start-1 row-start-1 flex size-full items-center justify-center whitespace-nowrap font-semibold leading-none tabular-nums text-foreground [&_svg]:size-[45%]",
                classNames?.value,
              )}
              style={{ fontSize: size === "xs" ? undefined : ringFontSize(centered, px, stroke, size) }}
            >
              {centered}
            </span>
          )}
        </ProgressPrimitive.Root>
        {side
          ? textColumn
          : (hasDescription || (outsideValue && showValue)) && (
              <div data-slot="progress-footer" className="flex min-w-0 flex-col items-center gap-0.5 text-center">
                {outsideValue && valueNode(false)}
                {descriptionNode}
              </div>
            )}
      </div>
    );
  }

  const trackBase = cn("relative w-full", TRACK_SIZES[size]);

  return (
    <div
      {...rootProps}
      className={cn("flex w-full min-w-0 flex-col", STACK_GAPS[size], toneClasses[tone], className)}
      {...props}
    >
      <Keyframes />
      {(hasLabel || (showValue && text != null)) && (
        <div
          data-slot="progress-header"
          className={cn(
            "flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5",
            classNames?.header,
          )}
        >
          {labelNode}
          {valueNode(true)}
        </div>
      )}
      {variant === "segmented" ? (
        <ProgressPrimitive.Root
          data-slot="progress-track"
          {...a11y}
          className={cn(
            "grid grid-flow-col auto-cols-fr rtl:-scale-x-100",
            trackBase,
            SEGMENT_GAPS[size],
            classNames?.track,
          )}
        >
          {Array.from({ length: segments }, (_, index) =>
            indeterminate ? (
              <div
                key={index}
                data-slot="progress-segment"
                className={cn(
                  "h-full animate-pulse bg-[color:color-mix(in_oklab,var(--tone)_25%,transparent)] motion-reduce:animate-none",
                  radius,
                  classNames?.segment,
                )}
                style={{ animationDelay: `${index * 120}ms` }}
              />
            ) : (
              <Segment
                key={index}
                index={index}
                count={segments}
                progress={progress}
                radius={radius}
                className={classNames?.segment}
                indicatorClassName={classNames?.indicator}
              />
            ),
          )}
        </ProgressPrimitive.Root>
      ) : (
        <ProgressPrimitive.Root
          data-slot="progress-track"
          {...a11y}
          // rtl fills from the right: the whole track mirrors
          className={cn(
            "overflow-hidden bg-foreground/10 rtl:-scale-x-100",
            trackBase,
            radius,
            classNames?.track,
          )}
        >
          {indeterminate ? (
            <>
              <div
                data-slot="progress-indicator"
                className={cn(
                  "ui-progress-slide absolute inset-y-0 start-0 w-2/5 bg-[color:var(--tone)] motion-reduce:hidden",
                  radius,
                  classNames?.indicator,
                )}
              />
              {/* reduced motion: a full-width bar pulsing in place of the slide */}
              <div
                aria-hidden
                className={cn("absolute inset-0 hidden animate-pulse bg-[color:color-mix(in_oklab,var(--tone)_50%,transparent)] motion-reduce:block", radius)}
              />
            </>
          ) : (
            <>
              {bufferFraction !== null && (
                <BufferFill progress={bufferProgress} radius={radius} />
              )}
              <LinearFill
                progress={progress}
                radius={radius}
                striped={striped && !complete}
                className={classNames?.indicator}
              />
            </>
          )}
        </ProgressPrimitive.Root>
      )}
      {descriptionNode}
    </div>
  );
}

function BufferFill({ progress, radius }: { progress: MotionValue<number>; radius: string }) {
  const x = useTransform(progress, (v) => `${(v - 1) * 100}%`);
  return (
    <motion.div
      data-slot="progress-buffer"
      aria-hidden
      className={cn("absolute inset-0 bg-[color:color-mix(in_oklab,var(--tone)_25%,transparent)]", radius)}
      style={{ x }}
    />
  );
}

// React 18: ref-urile ajung la randare doar prin forwardRef (vezi lib/with-ref.ts)
export const ProgressRing = withRef(ProgressRingRender, "ProgressRing");
export const Progress = withRef(ProgressRender, "Progress");

export default Progress;
